import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import QuoteCommentsPanel from '../components/QuoteCommentsPanel';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, IndianRupee, Truck, Edit3, Save, FileText, Search, FileSpreadsheet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MaterialBillingPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if user can edit (Account Manager or Super Admin only)
  const canEdit = user?.roles?.some(role => ['Account Manager', 'Super Admin'].includes(role));
  
  // Approved Amount billing sheet state
  const [amountSheetOpen, setAmountSheetOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [amountBasic, setAmountBasic] = useState('');
  const [amountGst, setAmountGst] = useState('');
  const [amountVoucherNo, setAmountVoucherNo] = useState('');
  const [savingAmount, setSavingAmount] = useState(false);
  
  // Bus Fare billing sheet state
  const [busSheetOpen, setBusSheetOpen] = useState(false);
  const [busVoucherNo, setBusVoucherNo] = useState('');
  const [savingBus, setSavingBus] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Export states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchApprovedMaterialRequests();
  }, []);

  const fetchApprovedMaterialRequests = async () => {
    try {
      const response = await axios.get(`${API}/material-requests/approved-for-billing`);
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load material requests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Open Approved Amount billing sheet
  const openAmountSheet = (request) => {
    setSelectedRequest(request);
    setAmountBasic(request.billing_basic_amount || '');
    setAmountGst(request.billing_gst_amount || '');
    setAmountVoucherNo(request.billing_voucher_no || '');
    setAmountSheetOpen(true);
  };

  // Open Bus Fare billing sheet
  const openBusSheet = (request) => {
    setSelectedRequest(request);
    setBusVoucherNo(request.bus_fare_voucher_no || '');
    setBusSheetOpen(true);
  };

  // Save Approved Amount billing details
  const handleSaveAmountBilling = async () => {
    if (!selectedRequest) return;
    
    setSavingAmount(true);
    try {
      await axios.put(`${API}/material-requests/${selectedRequest.id}/billing-amount`, {
        basic_amount: parseFloat(amountBasic) || 0,
        gst_amount: parseFloat(amountGst) || 0,
        voucher_no: amountVoucherNo
      });
      toast.success('Billing details saved successfully');
      setAmountSheetOpen(false);
      fetchApprovedMaterialRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save billing details');
    } finally {
      setSavingAmount(false);
    }
  };

  // Save Bus Fare billing details
  const handleSaveBusBilling = async () => {
    if (!selectedRequest) return;
    
    setSavingBus(true);
    try {
      await axios.put(`${API}/material-requests/${selectedRequest.id}/billing-bus-fare`, {
        voucher_no: busVoucherNo
      });
      toast.success('Bus fare voucher saved successfully');
      setBusSheetOpen(false);
      fetchApprovedMaterialRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save voucher');
    } finally {
      setSavingBus(false);
    }
  };

  // Check if billing is complete
  const isBillingComplete = (request) => {
    return request.billing_voucher_no && request.bus_fare_voucher_no;
  };

  // Export to Excel handler
  const handleExportToExcel = async () => {
    if (!exportFromDate || !exportToDate) {
      toast.error('Please select both From and To dates');
      return;
    }
    
    if (new Date(exportFromDate) > new Date(exportToDate)) {
      toast.error('From date cannot be after To date');
      return;
    }
    
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/material-billing/export?from_date=${exportFromDate}&to_date=${exportToDate}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Material_Billing_${exportFromDate}_to_${exportToDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully');
      setExportDialogOpen(false);
      setExportFromDate('');
      setExportToDate('');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Filter requests based on search query
  const filteredRequests = requests.filter(request => 
    !searchQuery || request.quote_no?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DC143C]"></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="material-billing-page">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Billing of Material
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredRequests.length} approved material request(s) found
              {searchQuery && ` (filtered from ${requests.length})`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setExportDialogOpen(true)}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </div>

        {/* Search Box */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by Quote Number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-quote-input"
          />
        </div>

        <Card className="overflow-hidden glass-table">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="material-billing-table">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-medium">Quote No</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Requested By</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Material Requested</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Materials To Deliver</th>
                  <th className="px-4 py-4 text-right text-sm font-medium">Approved Amount</th>
                  <th className="px-4 py-4 text-right text-sm font-medium">Bus/Transport Fare</th>
                  <th className="px-4 py-4 text-right text-sm font-medium">Total Amount</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Status</th>
                  <th className="px-4 py-4 text-left text-sm font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">
                      {searchQuery ? `No approved material requests found for "${searchQuery}"` : 'No approved material requests found'}
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm font-medium">{request.quote_no}</span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {request.requested_by_name}
                      </td>
                      <td className="px-4 py-4 text-sm max-w-[150px]">
                        <span className="break-words">{request.remarks || '-'}</span>
                      </td>
                      <td className="px-4 py-4 text-sm max-w-[200px]">
                        <span className="break-words">{request.approval_remarks || '-'}</span>
                      </td>
                      
                      {/* Approved Amount - Clickable */}
                      {/* Approved Amount - Clickable for Account Manager only */}
                      <td className="px-4 py-4 text-sm text-right">
                        {canEdit ? (
                          <button
                            onClick={() => openAmountSheet(request)}
                            className="group flex items-center justify-end gap-1 w-full hover:text-[#DC143C] transition-colors"
                            data-testid={`amount-btn-${request.id}`}
                          >
                            <span className="font-mono">
                              ₹{((request.approved_amount || 0) - (request.bus_fare || 0)).toLocaleString('en-IN')}
                            </span>
                            {request.billing_voucher_no ? (
                              <CheckCircle2 className="h-3 w-3 text-green-600" />
                            ) : (
                              <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </button>
                        ) : (
                          <span className="font-mono flex items-center justify-end gap-1">
                            ₹{((request.approved_amount || 0) - (request.bus_fare || 0)).toLocaleString('en-IN')}
                            {request.billing_voucher_no && <CheckCircle2 className="h-3 w-3 text-green-600" />}
                          </span>
                        )}
                        {request.billing_voucher_no && (
                          <div className="text-xs text-green-600 mt-1">
                            V: {request.billing_voucher_no}
                          </div>
                        )}
                      </td>
                      
                      {/* Bus/Transport Fare - Clickable for Account Manager only */}
                      <td className="px-4 py-4 text-sm text-right">
                        {canEdit ? (
                          <button
                            onClick={() => openBusSheet(request)}
                            className="group flex items-center justify-end gap-1 w-full hover:text-[#DC143C] transition-colors"
                            data-testid={`bus-btn-${request.id}`}
                          >
                            <span className="font-mono">
                              ₹{(request.bus_fare || 0).toLocaleString('en-IN')}
                            </span>
                            {request.bus_fare_voucher_no ? (
                              <CheckCircle2 className="h-3 w-3 text-green-600" />
                            ) : (
                              <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </button>
                        ) : (
                          <span className="font-mono flex items-center justify-end gap-1">
                            ₹{(request.bus_fare || 0).toLocaleString('en-IN')}
                            {request.bus_fare_voucher_no && <CheckCircle2 className="h-3 w-3 text-green-600" />}
                          </span>
                        )}
                        {request.bus_fare_voucher_no && (
                          <div className="text-xs text-green-600 mt-1">
                            V: {request.bus_fare_voucher_no}
                          </div>
                        )}
                      </td>
                      
                      <td className="px-4 py-4 text-sm text-right font-mono font-semibold">
                        <span className="inline-flex items-center">
                          ₹{(request.approved_amount || 0).toLocaleString('en-IN')}
                          <QuoteCommentsPanel quoteId={request.quote_id} quoteNo={request.quote_no} />
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={`${isBillingComplete(request) ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-green-100 text-green-800 border-green-200'}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {isBillingComplete(request) ? 'BILLED' : 'APPROVED'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-sm whitespace-nowrap">
                        {request.dm_approved_at 
                          ? new Date(request.dm_approved_at).toLocaleDateString()
                          : new Date(request.created_at).toLocaleDateString()
                        }
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Approved Amount Billing Sheet */}
      <Sheet open={amountSheetOpen} onOpenChange={setAmountSheetOpen}>
        <SheetContent side="right" className="w-[400px] sm:w-[450px]">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-green-600" />
              Approved Amount Billing
            </SheetTitle>
            {selectedRequest && (
              <p className="text-sm text-muted-foreground">
                Quote: {selectedRequest.quote_no}
              </p>
            )}
          </SheetHeader>
          
          <div className="space-y-6 py-6">
            {/* Display original approved amount */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 font-medium">Approved Amount</p>
              <p className="text-2xl font-bold text-green-800">
                ₹{(selectedRequest?.approved_amount || 0).toLocaleString('en-IN')}
              </p>
            </div>
            
            {/* Basic Amount */}
            <div className="space-y-2">
              <Label htmlFor="basic_amount">Basic Amount (₹)</Label>
              <Input
                id="basic_amount"
                type="number"
                step="0.01"
                value={amountBasic}
                onChange={(e) => setAmountBasic(e.target.value)}
                placeholder="Enter basic amount"
              />
            </div>
            
            {/* GST Amount */}
            <div className="space-y-2">
              <Label htmlFor="gst_amount">GST Amount (₹)</Label>
              <Input
                id="gst_amount"
                type="number"
                step="0.01"
                value={amountGst}
                onChange={(e) => setAmountGst(e.target.value)}
                placeholder="Enter GST amount"
              />
              {amountBasic && amountGst && (
                <p className="text-xs text-blue-600">
                  Total: ₹{((parseFloat(amountBasic) || 0) + (parseFloat(amountGst) || 0)).toLocaleString('en-IN')}
                </p>
              )}
            </div>
            
            {/* Voucher No */}
            <div className="space-y-2">
              <Label htmlFor="voucher_no">Voucher No *</Label>
              <Input
                id="voucher_no"
                value={amountVoucherNo}
                onChange={(e) => setAmountVoucherNo(e.target.value)}
                placeholder="Enter voucher number"
              />
            </div>
            
            {/* Save Button */}
            <div className="flex gap-2 pt-4 border-t">
              <Button
                onClick={handleSaveAmountBilling}
                disabled={savingAmount || !amountVoucherNo.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingAmount ? 'Saving...' : 'Save Billing Details'}
              </Button>
              <Button variant="outline" onClick={() => setAmountSheetOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Bus/Transport Fare Billing Sheet */}
      <Sheet open={busSheetOpen} onOpenChange={setBusSheetOpen}>
        <SheetContent side="right" className="w-[400px] sm:w-[450px]">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              Bus/Transport Fare Billing
            </SheetTitle>
            {selectedRequest && (
              <p className="text-sm text-muted-foreground">
                Quote: {selectedRequest.quote_no}
              </p>
            )}
          </SheetHeader>
          
          <div className="space-y-6 py-6">
            {/* Display bus fare */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700 font-medium">Bus/Transport Fare</p>
              <p className="text-2xl font-bold text-blue-800">
                ₹{(selectedRequest?.bus_fare || 0).toLocaleString('en-IN')}
              </p>
              {selectedRequest?.bus_details && (
                <p className="text-sm text-blue-600 mt-2">
                  Details: {selectedRequest.bus_details}
                </p>
              )}
            </div>
            
            {/* Voucher No */}
            <div className="space-y-2">
              <Label htmlFor="bus_voucher_no">Voucher No *</Label>
              <Input
                id="bus_voucher_no"
                value={busVoucherNo}
                onChange={(e) => setBusVoucherNo(e.target.value)}
                placeholder="Enter voucher number"
              />
            </div>
            
            {/* Save Button */}
            <div className="flex gap-2 pt-4 border-t">
              <Button
                onClick={handleSaveBusBilling}
                disabled={savingBus || !busVoucherNo.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingBus ? 'Saving...' : 'Save Voucher'}
              </Button>
              <Button variant="outline" onClick={() => setBusSheetOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Export to Excel Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Export to Excel</DialogTitle>
            <DialogDescription>
              Select a date range to export material billing data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="export_from_date">From Date *</Label>
              <Input
                id="export_from_date"
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                data-testid="export-from-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="export_to_date">To Date *</Label>
              <Input
                id="export_to_date"
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                data-testid="export-to-date"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleExportToExcel}
                disabled={exporting || !exportFromDate || !exportToDate}
                className="flex-1"
                data-testid="confirm-export-btn"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setExportDialogOpen(false);
                  setExportFromDate('');
                  setExportToDate('');
                }}
                disabled={exporting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
